import React, { Fragment, useMemo, useRef, useState } from 'react'

import {
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableContainerProps,
  TableFooter,
  TableHead,
  TableProps,
  TableRow,
} from '@material-ui/core'

import { ClassNameMap } from '@material-ui/styles'

import { ArrowDownward, ArrowDropDown, ArrowDropUp, ArrowUpward } from '@material-ui/icons'

import { CreateCSSProperties } from '@material-ui/core/styles/withStyles'

import { makeAppStyles } from '../../themes'

import { SecondaryIconTooltip } from '../Tooltips'

import { get, identity, isArray, sortBy } from 'lodash-es'

import {
  CellRenderer,
  CellRenderers,
  Column,
  DefaultCellRenderer,
  TableSortConfig,
} from './typings'


type SimpleTableClassNames = (
  | 'clickable'
  | 'columnHeaderContent'
  | 'columnHeaderContentCenter'
  | 'columnHeaderContentLeft'
  | 'columnHeaderContentRight'
  | 'footerRow'
  | 'groupedChildRow'
  | 'groupedRow'
  | 'groupedTableBody'
  | 'groupToggle'
  | 'groupToggleCellContainer'
  | 'groupToggleCellContent'
  | 'headerCell'
  | 'headerTooltip'
  | 'noTextWrap'
  | 'sortIcon'
  | 'stickyHeader'
  | 'stickyHeaderAlways'
  | 'stickyTableBodyCell'
  | 'stickyTableHeaderCell'
  | 'tableCell'
  | 'tableFooter'
  | 'tableHead'
  | 'tableRow'
  | 'tableRowClickable'
  | 'zIndexUnset'
)

type FontWeightLabel = 'light' | 'regular' | 'medium' | 'bold'
type CellFontWeight = number | 'inherit' | FontWeightLabel

type ThemeTypographyFontWeightProperty = (
  | 'fontWeightLight'
  | 'fontWeightRegular'
  | 'fontWeightMedium'
  | 'fontWeightBold'
)

const themeFontWeightMap: Record<FontWeightLabel, ThemeTypographyFontWeightProperty> = {
  light: 'fontWeightLight',
  regular: 'fontWeightRegular',
  medium: 'fontWeightMedium',
  bold: 'fontWeightBold',
}

const isFontWeightLabel = (fontWeight: CellFontWeight): fontWeight is FontWeightLabel => (
  !!themeFontWeightMap[fontWeight as FontWeightLabel]
)

type GroupColor = 'primary' | 'secondary' | 'neutral' | [string, string] 

type GroupedSimpleTableStylingProps = {
  groupColor?: 'primary' | 'secondary' | 'neutral' | [string, string]
}

export type SimpleTableProps<T extends object> = (
  Partial<
    TableProps
  > & {
    columns: Column<T>[]
    rows: T[]
    footerRows?: T[] | null
    getAdditionalHeaders?: (props: { classes: Record<string, string> }) => JSX.Element
    renderers?: CellRenderers<T>
    defaultRenderer?: DefaultCellRenderer<T>
    classes?: Partial<ClassNameMap<SimpleTableClassNames>>
    pinFirstColumn?: boolean
    sortable?: boolean
    initialSortConfig?: TableSortConfig<T>
    onRowClick?: (event: React.MouseEvent, row: T) => void
    getRowKey?: (row: T) => string | number
    cellFontWeight?: CellFontWeight
    TableContainerProps?: TableContainerProps
    unsetStickyHeaderZIndex?: boolean
    wrapCellText?: boolean
  } & (
    {
      grouped?: never
      groupedFooter?: never
      expandedByDefault?: never
      getRowGroup?: never
      hasGroupMembers?: never
      onRowExpand?: never
      onRowCollapse?: never
      groupColor?: never
      groupTextColor?: never
    } | {
      grouped: true
      groupedFooter?: boolean
      expandedByDefault?: boolean
      getRowGroup: (row: T) => T[]
      hasGroupMembers?: (row: T) => boolean
      onRowExpand?: (row: T) => void
      onRowCollapse?: (row: T) => void
      groupColor?: GroupColor
      groupTextColor?: string
    }
  )
)

const stickyCellShadow: CreateCSSProperties = {
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    right: 0,
    height: '100%',
    width: 5,
    background: `linear-gradient(to right, ${
      'rgba(0, 0, 0, 0.25), rgba(0, 0, 0, 0.25) 10%, rgba(0, 0, 0, 0.1) 60%, rgba(0, 0, 0, 0) 100%'
    })`,
  },
}

const useStyles = makeAppStyles<{ cellFontWeight: CellFontWeight, groupColor?: GroupColor, groupTextColor?: string },
  SimpleTableClassNames
>( theme => ({
  tableRow: {
    background: theme.palette.background.paper,
    '&:nth-child(odd)': {
      background: theme.palette.action.disabledBackground,
    },
  },
  tableHead: {},
  groupedTableBody: ({ groupColor = 'neutral', groupTextColor }): CreateCSSProperties => {
    let even = undefined, odd = undefined, color = undefined
    if( isArray(groupColor) ){
      even = groupColor[0]
      odd = groupColor[1]
      color = groupTextColor || theme.palette.getContrastText(even)
    }
    else{
      const referencedPalette = theme.palette[groupColor]
      even = referencedPalette.light
      odd = referencedPalette.main
      color = groupTextColor || referencedPalette.contrastText
    }
    return {
      '& > .MuiTableRow-root:first-child': {
        color,
        background: even,
      },
      '&:nth-child(odd) > .MuiTableRow-root:first-child': {
        background: odd,
      },
    }
  },
  groupedRow: {
    '& .MuiTableCell-body:first-child': {
      paddingLeft: theme.spacing(1),
    },
    '& .MuiTableCell-footer:first-child': {
      paddingLeft: theme.spacing(1),
    },
  },
  groupedChildRow: {
    '& .MuiTableCell-body:first-child': {
      paddingLeft: 44,
    },
    '& .MuiTableCell-footer:first-child': {
      paddingLeft: 44,
    },
    '& $tableCell': {
      fontWeight: theme.typography.fontWeightMedium,
    },
  },
  groupToggle: {
    marginTop: -4,
    marginBottom: -4,
    marginLeft: -2,
    zIndex: 'unset',
  },
  groupToggleCellContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  groupToggleCellContent: {
    display: 'flex',
    marginLeft: 6,
  },
  tableFooter: {
    position: 'relative',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: -8,
      left: 0,
      height: 8,
      width: '100%',
      zIndex: theme.zIndex.appBar - 1,
      background: `linear-gradient(to top, ${
        'rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.4) 10%, rgba(0, 0, 0, 0.1) 60%, rgba(0, 0, 0, 0) 100%'
      })`,
    },
  },
  footerRow: {
    '& .MuiTableCell-footer': {
      ...(get(theme.appComponents, ['table', 'footer']) || {}),
      borderTop: '1px solid',
      '& .MuiTableCell-footer': {
        color: 'inherit',
      }
    },
    '&:first-of-type .MuiTableCell-footer': {
      borderTop: 'none',
    },
  },
  columnHeaderContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
  columnHeaderContentCenter: {
    justifyContent: 'center',
  },
  columnHeaderContentLeft: {
    justifyContent: 'flex-start',
  },
  columnHeaderContentRight: {
    justifyContent: 'flex-end',
  },
  clickable: {
    cursor: 'pointer',
    userSelect: 'none',
  },
  tableCell: ({ cellFontWeight }): CreateCSSProperties => ({
    fontWeight: (
      isFontWeightLabel(cellFontWeight) ?
        theme.typography[themeFontWeightMap[cellFontWeight]] :
        cellFontWeight
    ),
    transition: theme.transitions.create('color'),
  }),
  noTextWrap: {
    textWrap: 'nowrap'
  },
  tableRowClickable: {
    cursor: 'pointer',
    userSelect: 'none',
    transition: theme.transitions.create('background-color'),
    '&:hover': {
      color: theme.palette.primary.contrastText,
      backgroundColor: theme.palette.primary.main,
      '& .MuiTableCell-body': {
        color: theme.palette.primary.contrastText,
      }
    },
  },
  headerCell: {
    fontWeight: theme.typography.fontWeightBold,
    fontSize: 14,
  },
  headerTooltip: {
    marginLeft: theme.spacing(0.5),
  },
  sortIcon: {
    fontSize: 16,
    marginLeft: 4,
    color: theme.palette.text.hint,
  },
  stickyHeaderAlways: {
    left: 'unset',
  },
  stickyHeader: {
    background: theme.palette.background.paper,
    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: -8,
      left: 0,
      width: '100%',
      height: 8,
      background: `linear-gradient(to bottom, ${
        'rgba(0, 0, 0, 0.35), rgba(0, 0, 0, 0.35) 10%, rgba(0, 0, 0, 0.1) 60%, rgba(0, 0, 0, 0) 100%'
      })`,
      zIndex: 2,
    },
    zIndex: theme.zIndex.appBar - 3,
  },
  zIndexUnset: {
    zIndex: 'unset',
  },
  stickyTableHeaderCell: {
    left: 0,
    position: 'sticky',
    zIndex: theme.zIndex.appBar - 1,
    paddingRight: 12,
    ...stickyCellShadow,
  },
  stickyTableBodyCell: {
    left: 0,
    position: 'sticky',
    zIndex: theme.zIndex.appBar - 2,
    background: 'inherit',
    paddingRight: 12,
    ...stickyCellShadow,
  },
}) )


function defaultCellRenderer<T extends object>(item: T, key: keyof T): React.ReactNode {
  return item[key] as React.ReactNode
}


const flipSortOrder = (order: 'ASC' | 'DESC'): 'ASC' | 'DESC' => (
  order === 'ASC' ? 'DESC' : 'ASC'
)

type RowGroup<T extends object> = {
  parentRow: T | null
  childRows: T[]
  hasGroupMembers: boolean
}

type GroupedRowsProps<T extends object> = {
  classes: ClassNameMap<SimpleTableClassNames>
  expandedState: Record<string | number, boolean>
  setExpandedState: React.Dispatch<React.SetStateAction<Record<string | number, boolean>>>
  groupKey: string | number
  tableBodyCellClassName: string
  parentRowClassName?: string
  childRowClassName?: string
} & RowGroup<T> & Omit<
  SimpleTableProps<T>,
  | 'rows' | 'footerRows' | 'grouped' | 'groupedFooter' | 'hasGroupMembers' | 'groupColor' | 'groupTextColor'
  | 'getAdditionalHeaders'
>

function GroupedRows<T extends object>({
  groupKey,
  parentRow,
  hasGroupMembers,
  childRows,
  parentRowClassName,
  childRowClassName,
  tableBodyCellClassName,
  classes,
  expandedByDefault,
  expandedState,
  setExpandedState,
  onRowClick,
  getRowKey,
  columns,
  renderers,
  defaultRenderer = defaultCellRenderer,
  pinFirstColumn,
  onRowExpand,
  onRowCollapse,
}: GroupedRowsProps<T>): JSX.Element {
  const grouped = !!parentRow
  return (
    <>
      { parentRow && (
        <TableRow
          className={parentRowClassName}
          key={`group-${groupKey}`}
          onClick={onRowClick && ((e: React.MouseEvent): void => onRowClick(e, parentRow))}>

          { columns.map( ({ key, label, ...props }, columnIndex) => {

            let cellContent = null

            if( renderers && renderers[key] ){
              const Renderer = renderers[key] as CellRenderer<T>
              cellContent = <Renderer {...parentRow} />
            }else{
              cellContent = defaultRenderer(parentRow, key)
            }

            let className = tableBodyCellClassName
            if( columnIndex === 0 && pinFirstColumn ){
              className += (' ' + classes.stickyTableBodyCell)
            }

            const currentExpandState = Boolean(
              get(expandedState, groupKey, expandedByDefault)
            )

            return (
              <TableCell
                className={className}
                key={`${groupKey}-${key}`}
                {...props}>
                { grouped && columnIndex === 0 ? (
                  <span
                    className={classes.groupToggleCellContainer}>
                    <IconButton
                      color='inherit'
                      size='small'
                      className={classes.groupToggle}
                      disabled={!hasGroupMembers}
                      onClick={(e): void => {
                        e.preventDefault()
                        e.stopPropagation()
                        if( currentExpandState === false && onRowExpand ){
                          onRowExpand(parentRow)
                        }else if( currentExpandState === true && onRowCollapse ){
                          onRowCollapse(parentRow)
                        }
                        setExpandedState( prev => ({...prev, [groupKey]: !currentExpandState }))
                      }}>
                      { currentExpandState ? <ArrowDropUp /> : <ArrowDropDown /> }
                    </IconButton>
                    <span className={classes.groupToggleCellContent}>
                      { cellContent }
                    </span>
                  </span>
                ) : cellContent }
              </TableCell>
            )
          })}

        </TableRow>
      )}

      { (!grouped || get(expandedState, groupKey, expandedByDefault)) && childRows.map( (childRow, j) => {
        const childKey = getRowKey ? getRowKey(childRow) : j
        let className = childRowClassName || classes.tableRow
        if( grouped ){
          className += (' ' + classes.groupedChildRow)
        }
        if( onRowClick ){
          className += (' ' + classes.tableRowClickable)
        }
        return (
          <TableRow
            className={className}
            key={`group-${groupKey}-child-${childKey}`}
            onClick={onRowClick && ((e: React.MouseEvent): void => onRowClick(e, childRow))}>

            { columns.map( ({ key, label, ...props }, columnIndex) => {

              let cellContent = null

              if( renderers && renderers[key] ){
                const Renderer = renderers[key] as CellRenderer<T>
                cellContent = <Renderer {...childRow} />
              }else{
                cellContent = defaultRenderer(childRow, key)
              }

              let className = tableBodyCellClassName
              if( columnIndex === 0 && pinFirstColumn ){
                className += (' ' + classes.stickyTableBodyCell)
              }

              return (
                <TableCell
                  className={className}
                  key={`${groupKey}-${key}`}
                  {...props}>
                  { cellContent }
                </TableCell>
              )
            })}

          </TableRow>
        )
      })}
    </>
  )
}


export function SimpleTable<T extends object>({
  columns,
  rows,
  grouped,
  groupedFooter = false,
  expandedByDefault = false,
  groupColor = 'neutral',
  groupTextColor,
  getRowGroup,
  hasGroupMembers,
  onRowExpand,
  onRowCollapse,
  footerRows,
  renderers,
  defaultRenderer = defaultCellRenderer,
  classes = {},
  initialSortConfig,
  sortable,
  onRowClick,
  getRowKey,
  TableContainerProps,
  unsetStickyHeaderZIndex = true,
  wrapCellText = true,
  pinFirstColumn = false,
  getAdditionalHeaders,
  cellFontWeight = 'bold',
  ...props
}: SimpleTableProps<T>): JSX.Element {

  const mergedClasses = {
    ...useStyles({ cellFontWeight, groupColor, groupTextColor }),
    ...classes,
  }

  let stickyHeaderClassName = `${mergedClasses.stickyHeader} ${mergedClasses.stickyHeaderAlways}`
  if( unsetStickyHeaderZIndex ){
    stickyHeaderClassName += (' ' + mergedClasses.zIndexUnset)
  }

  let headerCellClassName = mergedClasses.headerCell
  let tableBodyCellClassName = mergedClasses.tableCell
  if( !wrapCellText ){
    headerCellClassName += (' ' + mergedClasses.noTextWrap)
    tableBodyCellClassName += (' ' + mergedClasses.noTextWrap)
  }

  const [sortConfig, setSortConfig] = useState(initialSortConfig || null)

  // const columnsRef = useRef(columns)
  // columnsRef.current = columns

  // const groupAdapterRef = useRef(getRowGroup)
  // groupAdapterRef.current = getRowGroup

  // const groupMembershipTestRef = useRef(hasGroupMembers)
  // groupMembershipTestRef.current = hasGroupMembers

  const [expandedGroupState, setExpandedGroupState] = useState<Record<number | string, boolean>>({})

  let rowGroups: RowGroup<T>[] = []
  if( !grouped ){
    rowGroups = [{ parentRow: null, childRows: rows, hasGroupMembers: false }]
  }else{
    rowGroups = rows.map( parentRow => ({
      parentRow,
      childRows: getRowGroup && getRowGroup(parentRow) || [],
      hasGroupMembers: hasGroupMembers ? hasGroupMembers(parentRow) : true,
    })) 
  }

  let footerRowGroups: RowGroup<T>[] | null = null
  if( footerRows ){
    if( !grouped || !groupedFooter ){
      footerRowGroups = [{ parentRow: null, childRows: footerRows, hasGroupMembers: false }]
    }else{
      footerRowGroups = footerRows.map( parentRow => ({
        parentRow,
        childRows: getRowGroup && getRowGroup(parentRow) || [],
        hasGroupMembers: hasGroupMembers ? hasGroupMembers(parentRow) : true,
      }))
    }
  }

  // const rowGroups = useMemo(() => {
  //   if( !grouped ){
  //     return [{ parentRow: null, childRows: rows, hasGroupMembers: false }]
  //   }
  //   return rows.map( parentRow => ({
  //     parentRow,
  //     childRows: groupAdapterRef.current && groupAdapterRef.current(parentRow) || [],
  //     hasGroupMembers: groupMembershipTestRef.current ? groupMembershipTestRef.current(parentRow) : true,
  //   }))
  // }, [rows, grouped])

  let sortedRowGroups: RowGroup<T>[] = rowGroups.slice()

  if( sortConfig ){
    const { key } = sortConfig
    const column = columns.find( c => c.key === key )
    const getSortValue = column && column.getSortValue || identity
    const rowGroupsWithSortedChildRows = rowGroups.map( ({ parentRow, childRows, hasGroupMembers }) => {
      const sortedRows = sortBy(
        childRows,
        row => {
          const value = getSortValue(row[sortConfig.key])
          if( !value ) return Number(value)
          return value
        }
      )
      if( sortConfig.order !== 'ASC' ){
        sortedRows.reverse()
      }
      return {
        parentRow,
        hasGroupMembers,
        childRows: sortedRows
      }
    })

    const sortedGroups = sortBy(
      rowGroupsWithSortedChildRows,
      row => {
        if( !row.parentRow ){
          return null
        }
        const value = getSortValue(row.parentRow[sortConfig.key])
        if( !value ) return Number(value)
        return value
      }
    )
    if( sortConfig.order !== 'ASC' ){
      sortedGroups.reverse()
    }
    sortedRowGroups = sortedGroups
  }

  // const sortedRowGroups = useMemo(() => {
  //   if( !sortConfig ){
  //     return rowGroups
  //   }
  //   const { key } = sortConfig
  //   const column = columnsRef.current.find( c => c.key === key )
  //   const getSortValue = column && column.getSortValue || identity
  //   const rowGroupsWithSortedChildRows = rowGroups.map( ({ parentRow, childRows, hasGroupMembers }) => {
  //     const sortedRows = sortBy(
  //       childRows,
  //       row => {
  //         const value = getSortValue(row[sortConfig.key])
  //         if( !value ) return Number(value)
  //         return value
  //       }
  //     )
  //     if( sortConfig.order !== 'ASC' ){
  //       sortedRows.reverse()
  //     }
  //     return {
  //       parentRow,
  //       hasGroupMembers,
  //       childRows: sortedRows
  //     }
  //   })

  //   const sortedGroups = sortBy(
  //     rowGroupsWithSortedChildRows,
  //     row => {
  //       if( !row.parentRow ){
  //         return null
  //       }
  //       const value = getSortValue(row.parentRow[sortConfig.key])
  //       if( !value ) return Number(value)
  //       return value
  //     }
  //   )
  //   if( sortConfig.order !== 'ASC' ){
  //     sortedGroups.reverse()
  //   }
  //   return sortedGroups
  // }, [rowGroups, sortConfig])

  return (
    <TableContainer {...TableContainerProps}>
      <Table
        {...props}>

        { getAdditionalHeaders && getAdditionalHeaders({ classes: mergedClasses })}

        <TableHead
          className={mergedClasses.tableHead}>

          { columns.map( ({ key, label, tooltip, ...props }, columnIndex) => (
            <TableCell
              classes={{
                stickyHeader: (
                  columnIndex === 0 && pinFirstColumn ?
                    stickyHeaderClassName + ' ' + mergedClasses.stickyTableHeaderCell :
                    stickyHeaderClassName
                ),
              }}
              className={headerCellClassName}
              key={key}
              {...props}>
              <span
                className={
                  [
                    mergedClasses.columnHeaderContent,
                    props.align === 'center' && mergedClasses.columnHeaderContentCenter,
                    props.align === 'right' && mergedClasses.columnHeaderContentRight,
                    props.align === 'left' && mergedClasses.columnHeaderContentLeft,
                    sortable && mergedClasses.clickable
                  ].filter(Boolean).join(' ')
                }
                onClick={(): void => {
                  if( sortable ){
                    if( !sortConfig ){
                      setSortConfig({
                        key,
                        order: 'DESC',
                      })
                    }else{
                      if( sortConfig.key === key ){
                        setSortConfig({
                          key,
                          order: flipSortOrder(sortConfig.order)
                        })
                      }else{
                        setSortConfig({
                          key,
                          order: sortConfig.order,
                        })
                      }
                    }
                  }
                }}>
                <Fragment>
                  { label }
                  { tooltip && (
                    <SecondaryIconTooltip
                      className={mergedClasses.headerTooltip}
                      title={tooltip} />
                  )}
                  { !!(sortable && sortConfig && sortConfig.key === key) && (
                    sortConfig.order === 'ASC' ?
                      <ArrowUpward className={mergedClasses.sortIcon} /> :
                      <ArrowDownward className={mergedClasses.sortIcon} />
                  )}
                </Fragment>
              </span>
            </TableCell>
          ))}

        </TableHead>

        { sortedRowGroups.map( ({ parentRow, childRows, hasGroupMembers }, i) => {
          const groupKey = parentRow && getRowKey ? getRowKey(parentRow) : i
          const className = grouped ? mergedClasses.groupedTableBody : undefined
          let parentRowClassName = (
            grouped ?
              mergedClasses.groupedRow || mergedClasses.tableRow :
              mergedClasses.tableRow
          )
          if( onRowClick ){
            parentRowClassName += (' ' + mergedClasses.tableRowClickable)
          }

          return (
            <TableBody
              key={groupKey}
              className={className}>
              <GroupedRows
                columns={columns}
                renderers={renderers}
                pinFirstColumn={pinFirstColumn}
                groupKey={groupKey}
                getRowKey={getRowKey}
                parentRow={parentRow}
                childRows={childRows}
                hasGroupMembers={hasGroupMembers}
                classes={mergedClasses}
                className={className}
                parentRowClassName={parentRowClassName}
                expandedState={expandedGroupState}
                expandedByDefault={expandedByDefault}
                setExpandedState={setExpandedGroupState}
                tableBodyCellClassName={tableBodyCellClassName}
                onRowClick={onRowClick}
                onRowCollapse={onRowCollapse}
                onRowExpand={onRowExpand}
                defaultRenderer={defaultRenderer} />
            </TableBody>
          )
        })}

        { !!(footerRowGroups && footerRowGroups.length) && (
          <TableFooter className={mergedClasses.tableFooter}>
            { footerRowGroups.map( ({ parentRow, childRows, hasGroupMembers }, i) => {
              const groupKey = parentRow && getRowKey ? getRowKey(parentRow) : `footer-${i}`
              let parentRowClassName = mergedClasses.footerRow || mergedClasses.tableRow
              if( grouped ){
                parentRowClassName += (' ' + mergedClasses.groupedRow)
              }
              if( onRowClick ){
                parentRowClassName += (' ' + mergedClasses.tableRowClickable)
              }

              return (
                <GroupedRows
                  key={groupKey}
                  columns={columns}
                  renderers={renderers}
                  pinFirstColumn={pinFirstColumn}
                  groupKey={groupKey}
                  getRowKey={getRowKey}
                  parentRow={parentRow}
                  childRows={childRows}
                  hasGroupMembers={hasGroupMembers}
                  classes={mergedClasses}
                  parentRowClassName={parentRowClassName}
                  childRowClassName={mergedClasses.footerRow}
                  expandedState={expandedGroupState}
                  expandedByDefault={expandedByDefault}
                  setExpandedState={setExpandedGroupState}
                  tableBodyCellClassName={tableBodyCellClassName}
                  onRowClick={onRowClick}
                  onRowCollapse={onRowCollapse}
                  onRowExpand={onRowExpand}
                  defaultRenderer={defaultRenderer} />
              )
            })}
          </TableFooter>
        )}

      </Table>
    </TableContainer>
  )
}
